/**
 * Modèle de persistance du dashboard (Phase 1).
 *
 * Principe : on ne persiste QUE ce qui permet (a) de placer le widget et
 * (b) de retrouver sa donnée temps réel. Aucune valeur temps réel (température,
 * humidité, counts…) n'est sauvegardée : elle est re-fetchée au rendu via `ref`.
 *
 * - `ref`     : références métier (ex. buildingId) → la "clé" vers la donnée live
 * - `display` : config d'affichage pure (showX, chartType, title, period…)
 * - tout le reste des props (données temps réel) est volontairement ignoré
 */
import type { GridStackOptions, GridStackWidget } from "gridstack";
import {
  getWidgetCatalogEntry,
  getWidgetMinSize,
  WidgetTypeId,
} from "./widgetCatalog";
import { parseWidgetContent } from "./widgetDraft";

export const DASHBOARD_CONFIG_VERSION = 1;

export type WidgetRef = {
  /** Référence du widget régulateur : uuid du produit, clé de tout le reste (bâtiment, lot…). */
  deviceUuid?: string;
  buildingId?: number;
  buildingIds?: number[];
  location?: { latitude: number; longitude: number; name: string };
};

export type PersistedWidget = {
  id: string;
  type: WidgetTypeId;
  layout: { x: number; y: number; w: number; h: number };
  ref?: WidgetRef;
  display?: Record<string, unknown>;
};

export type DashboardConfig = {
  version: number;
  widgets: PersistedWidget[];
};

/**
 * Spécification de persistance par type de widget.
 * - `displayKeys` : clés de props (config d'affichage) à conserver
 * - `toRef`       : extrait les références métier depuis les props
 * - `applyRef`    : réinjecte les références dans les props au rendu
 *
 * Tout ce qui n'est ni dans `displayKeys` ni géré par `toRef` est considéré
 * comme de la donnée temps réel et n'est PAS persisté.
 */
type WidgetPersistenceSpec = {
  displayKeys: string[];
  toRef?: (props: Record<string, unknown>) => WidgetRef | undefined;
  applyRef?: (ref: WidgetRef, props: Record<string, unknown>) => void;
};

const WIDGET_PERSISTENCE: Record<WidgetTypeId, WidgetPersistenceSpec> = {
  RegulatorWidget: {
    displayKeys: [
      "name",
      "layoutVariant",
      // `metrics` = les grandeurs cochées par l'utilisateur. C'est de la CONFIGURATION, pas
      // de la donnée temps réel : sans cette clé, le widget perdrait sa sélection au reload.
      "metrics",
      "primaryMetric",
      "secondaryMetric",
      "showChart",
      "chartType",
    ],
    // La référence métier du widget est le RÉGULATEUR, pas le bâtiment (décision du 2026-07-30 :
    // « ce n'est pas un widget building, mais un widget Régulateur »). C'est la clé de chargement
    // de tout le reste — bâtiment, lot ouvert, mesures, droits — résolu côté back en une requête.
    toRef: (props) =>
      typeof props.deviceUuid === "string" && props.deviceUuid !== ""
        ? { deviceUuid: props.deviceUuid }
        : undefined,
    applyRef: (ref, props) => {
      if (ref.deviceUuid !== undefined) props.deviceUuid = ref.deviceUuid;
    },
  },
  ChartWidget: {
    displayKeys: ["title", "periodLabel"],
    toRef: (props) =>
      Array.isArray(props.buildingIds)
        ? { buildingIds: props.buildingIds as number[] }
        : undefined,
    applyRef: (ref, props) => {
      if (ref.buildingIds !== undefined) props.buildingIds = ref.buildingIds;
    },
  },
  WeatherWidget: {
    // La localisation (lat/lon) est la référence pour fetcher la météo temps réel ;
    // les show* sont la config d'affichage (indicateurs cochés).
    displayKeys: [
      "showFeelsLike",
      "showMinMax",
      "showHumidity",
      "showWind",
      "showWindGusts",
      "showPressure",
      "showDewPoint",
      "showThi",
      "showUv",
      "showRainProbability",
      "showSunTimes",
      "showHourly",
      "showDaily",
    ],
    toRef: (props) =>
      typeof props.latitude === "number" && typeof props.longitude === "number"
        ? {
            location: {
              latitude: props.latitude,
              longitude: props.longitude,
              name: String(props.locationName ?? ""),
            },
          }
        : undefined,
    applyRef: (ref, props) => {
      if (ref.location) {
        props.latitude = ref.location.latitude;
        props.longitude = ref.location.longitude;
        props.locationName = ref.location.name;
      }
    },
  },
  ContactsWidget: {
    displayKeys: [],
  },
  AlarmsWidget: {
    // totalCount est une donnée temps réel → non persistée (re-fetchée).
    displayKeys: [],
  },
  NewsWidget: {
    // Aucune donnée live persistée : les actualités sont re-fetchées via la
    // catégorie. Seule la config d'affichage est conservée.
    displayKeys: ["category", "speed", "showSource", "showDate"],
  },
  TextWidget: {
    // Tout est de la config d'affichage (contenu + style), aucune donnée live.
    displayKeys: ["text", "style", "align", "background"],
  },
};

function pick(
  source: Record<string, unknown>,
  keys: string[]
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  keys.forEach((key) => {
    if (source[key] !== undefined) out[key] = source[key];
  });
  return out;
}

function getGridChildren(
  saved: GridStackOptions | GridStackWidget[] | undefined
): GridStackWidget[] {
  if (!saved) return [];
  if (Array.isArray(saved)) return saved;
  return saved.children ?? [];
}

/**
 * Convertit l'état GridStack courant (issu de `saveOptions()`) en config
 * persistable, en ne gardant que position/taille + ref + display.
 */
export function serializeDashboard(
  saved: GridStackOptions | GridStackWidget[] | undefined
): DashboardConfig {
  const widgets: PersistedWidget[] = [];

  getGridChildren(saved).forEach((child) => {
    const parsed = parseWidgetContent(child.content);
    if (!parsed || !child.id) return;

    const type = parsed.name as WidgetTypeId;
    const spec = WIDGET_PERSISTENCE[type];
    if (!spec) return;

    const props = parsed.props ?? {};

    widgets.push({
      id: child.id,
      type,
      layout: {
        x: child.x ?? 0,
        y: child.y ?? 0,
        w: child.w ?? 1,
        h: child.h ?? 1,
      },
      ref: spec.toRef?.(props),
      display: pick(props, spec.displayKeys),
    });
  });

  // Tri stable par id : sortie déterministe (comparaison fiable pour l'autosave).
  widgets.sort((a, b) => a.id.localeCompare(b.id));

  return { version: DASHBOARD_CONFIG_VERSION, widgets };
}

const widgetContent = (name: string, props: object) =>
  JSON.stringify({ name, props });

/**
 * Reconstruit les props runtime d'un widget à partir de sa config persistée.
 * Phase 1 : on part des défauts du catalogue (données de démo) puis on applique
 * display + ref. Phase 2 : les défauts seront remplacés par la donnée fetchée via ref.
 */
function toRuntimeProps(
  widget: PersistedWidget,
  defaultProps: Record<string, unknown>
): Record<string, unknown> {
  const spec = WIDGET_PERSISTENCE[widget.type];
  const props: Record<string, unknown> = {
    ...defaultProps,
    ...(widget.display ?? {}),
  };
  if (widget.ref && spec?.applyRef) spec.applyRef(widget.ref, props);
  return props;
}

/**
 * Convertit une config persistée en `children` GridStack prêts à être montés.
 * `getDefaultProps` fournit les props de démo par type (catalogue).
 */
export function deserializeDashboard(
  config: DashboardConfig,
  getDefaultProps: (type: WidgetTypeId) => Record<string, unknown>
): GridStackWidget[] {
  return config.widgets
    .filter((widget) => {
      // Un type absent du catalogue (widget d'une version antérieure, type renommé ou supprimé,
      // config corrompue) est ÉCARTÉ ici. Sans ce filtre il atteignait le rendu, où le registre
      // de composants renvoyait `undefined` : React plantait sur `<undefined />` et TOUT le
      // dashboard devenait inaccessible — pas seulement le widget périmé.
      if (getWidgetCatalogEntry(widget.type)) return true;
      console.warn(
        `Widget de type inconnu ignoré au chargement : "${widget.type}" (id ${widget.id})`
      );
      return false;
    })
    .map((widget) => {
      const { minW, minH } = getWidgetMinSize(widget.type);
      return {
        id: widget.id,
        x: widget.layout.x,
        y: widget.layout.y,
        // On force le respect de la taille minimale du type (un layout sauvegardé
        // plus petit, ou d'avant l'ajout du minimum, est remonté à la borne).
        w: minW ? Math.max(widget.layout.w, minW) : widget.layout.w,
        h: minH ? Math.max(widget.layout.h, minH) : widget.layout.h,
        minW,
        minH,
        content: widgetContent(
          widget.type,
          toRuntimeProps(widget, getDefaultProps(widget.type))
        ),
      };
    });
}
